import { MouseEvent as ReactMouseEvent } from 'react';
import { MOUSE_BUTTON_LEFT } from '~/engine/tool/MouseButton';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Stacking of the layer: above the image, below the crosshair and
 * annotation layers, so their handles stay reachable over a refused tile.
 */
const REFUSAL_LAYER_Z_INDEX = 5;

interface TilePoiRefusalLayerProps {
    /** The reason given when a left press lands on the tile. */
    reason: string;
    /**
     * The tile's canvas. The engine starts a press only when the canvas is
     * its target, so a press of another button is passed on to it.
     */
    canvas: HTMLCanvasElement | undefined;
}

/**
 * Covers a tile the armed point-of-interest tool cannot act through: the
 * not-allowed cursor over the whole tile, and the reason on a left press.
 * A press of another button is passed on to the tile canvas, so
 * window/level and pan keep working over a refused tile. The layer stays
 * below the crosshair and annotation handles. The wheel and the release
 * still reach the engine, because it resolves them by position, and the
 * context menu bubbles to the grid root, where the engine suppresses it.
 */
export default function TilePoiRefusalLayer({
    reason,
    canvas,
}: TilePoiRefusalLayerProps) {
    const onMouseDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
        if (event.button !== MOUSE_BUTTON_LEFT) {
            canvas?.dispatchEvent(
                new MouseEvent('mousedown', event.nativeEvent)
            );
            return;
        }
        enqueueSnackbar(reason, { variant: 'warning' });
    };
    return (
        <div
            data-testid="poi-refused-target"
            style={{
                position: 'absolute',
                inset: 0,
                cursor: 'not-allowed',
                zIndex: REFUSAL_LAYER_Z_INDEX,
            }}
            onMouseDown={onMouseDown}
        />
    );
}
